import { useEffect, useMemo, useRef, useState } from 'react';
import { spriteFor, type OceanHouse } from '../game/engine';
import { HOUSES } from '../game/houses';
import {
  FLOOR_Y, FLOORS, ITEMS, addedCost, MAX_ITEMS, ROOM_H, ROOM_W, WALLS, cleanInterior, drawItem, drawOrder, drawRoom, fishWhere, hitItem, newItem,
  type FloorId, type Interior, type ItemId, type RoomItem, type WallId,
} from '../game/interior';
import { drawIdle } from '../lib/fishIdle';
import { speciesById } from '../lib/aquarium';
import { sandDollars } from '../lib/shop';

// ─────────────────────────────────────────────────────────────────────────────
// Inside a fish house: its fish swim around the room (when they're not out in
// the ocean: see fishWhere), and the owner can decorate: wallpaper, floor, and
// things dragged around the room. Anyone in the class can look in.
// ─────────────────────────────────────────────────────────────────────────────

type Props = {
  house: OceanHouse;
  /** The owner can decorate; everyone else just looks. */
  editable: boolean;
  onSave?: (interior: Interior) => Promise<void>;
  onClose: () => void;
  /** Decoration prices by item id; without them, decorating is free (the dev demo). */
  prices?: Record<string, number>;
  /** The owner's sand dollars. */
  balance?: number;
};

const DOOR_X = 70;   // the doorway fish swim through, on the left of the room

function hash(s: string, salt: number): number {
  let h = 2166136261 ^ salt;
  for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 16777619); }
  return ((h >>> 0) % 100000) / 100000;
}

/** Draw the doorway: an arch in the left wall. */
function drawDoor(g: CanvasRenderingContext2D) {
  g.save();
  g.fillStyle = 'rgba(10,30,60,0.55)'; g.strokeStyle = '#1e2633'; g.lineWidth = 3;
  g.beginPath(); g.moveTo(DOOR_X - 45, FLOOR_Y); g.lineTo(DOOR_X - 45, FLOOR_Y - 110); g.arc(DOOR_X, FLOOR_Y - 110, 45, Math.PI, 0); g.lineTo(DOOR_X + 45, FLOOR_Y); g.closePath();
  g.fill(); g.stroke();
  g.restore();
}

export default function HouseRoom({ house, editable, onSave, onClose, prices = {}, balance }: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const saved = useMemo(() => cleanInterior(house.interior), [house.interior]);
  const [room, setRoom] = useState<Interior>(saved);
  const [editing, setEditing] = useState(false);
  const [sel, setSel] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [insideNow, setInsideNow] = useState(0);
  const roomRef = useRef(room); roomRef.current = room;
  const selRef = useRef(sel); selRef.current = sel;
  const editRef = useRef(editing); editRef.current = editing;
  const changed = JSON.stringify(room) !== JSON.stringify(saved);
  const cost = addedCost(saved, room, prices);
  const short = balance != null && cost > balance;

  useEffect(() => { setRoom(saved); }, [saved]);

  // Draw: the room, its things, and the fish that are home right now.
  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return;
    const g = c.getContext('2d')!;
    let raf = 0, alive = true, lastCount = -1;
    const t0 = performance.now();
    const frame = () => {
      if (!alive) return;
      raf = requestAnimationFrame(frame);
      const dpr = Math.min(2, window.devicePixelRatio || 1);
      const w = c.clientWidth, h = c.clientHeight;
      if (!w || !h) return;
      if (c.width !== Math.round(w * dpr)) { c.width = Math.round(w * dpr); c.height = Math.round(h * dpr); }
      const t = (performance.now() - t0) / 1000, now = Date.now() / 1000;
      const k = c.width / ROOM_W;
      g.setTransform(k, 0, 0, k, 0, 0);
      const r = roomRef.current;
      drawRoom(g, r.wall, r.floor, t);
      drawDoor(g);
      const items = drawOrder(r.items);
      const selected = selRef.current != null && editRef.current ? r.items[selRef.current] : null;
      // Wall things and rugs behind the fish; the rest after.
      const back = items.filter(it => it.k === 'painting' || it.k === 'window' || it.k === 'rug');
      const front = items.filter(it => !back.includes(it));
      for (const it of back) drawItem(g, it, t, it === selected);
      let count = 0;
      house.fish.forEach((f, i) => {
        const where = fishWhere(f.id, now);
        if (!where.inside) return;
        count++;
        const sprite = spriteFor(f.species);
        if (!sprite) return;
        const sp = speciesById(f.species);
        const h1 = hash(f.id, 1), h2 = hash(f.id, 2), h3 = hash(f.id, 3);
        const a = t / (5 + h1 * 5) + h2 * 6.28;
        const longest = 120 * sp.size;
        const fw = sprite.w >= sprite.h ? longest : (longest * sprite.w) / sprite.h;
        const fh = (fw * sprite.h) / sprite.w;
        let x = ROOM_W / 2 + 60 + Math.sin(a) * 320;
        let y = sp.habitat === 'bottom' ? FLOOR_Y - fh / 2 + 6
          : sp.habitat === 'float' ? 110 + h3 * 120 + Math.sin(a * 1.3) * 25
            : 140 + h3 * 220 + Math.sin(a * 1.9 + i) * 30;
        let facing: 1 | -1 = Math.cos(a) > 0 ? -1 : 1;
        const kd = where.door;
        if (kd > 0) {
          const dx = DOOR_X, dy = FLOOR_Y - 90;
          if (kd > 0.05) facing = dx < x ? 1 : -1;
          x += (dx - x) * kd; y += (dy - y) * kd;
        }
        g.save();
        if (kd > 0) g.globalAlpha = 1 - kd * 0.85;
        drawIdle(g, sprite, sp.profile, t + h1 * 10, { x, y, width: fw * (1 - kd * 0.6), facing, step: 2 / k });
        g.restore();
      });
      for (const it of front) drawItem(g, it, t, it === selected);
      if (count !== lastCount) { lastCount = count; setInsideNow(count); }
    };
    raf = requestAnimationFrame(frame);
    return () => { alive = false; cancelAnimationFrame(raf); };
  }, [house.fish]);

  // Decorating: tap a thing to select it, drag to move it.
  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return;
    let drag: { i: number; ox: number; oy: number } | null = null;
    const toRoom = (e: PointerEvent) => {
      const b = c.getBoundingClientRect();
      return { x: ((e.clientX - b.left) / b.width) * ROOM_W, y: ((e.clientY - b.top) / b.height) * ROOM_H };
    };
    const down = (e: PointerEvent) => {
      if (!editRef.current) return;
      const p = toRoom(e);
      const items = roomRef.current.items;
      // Topmost first: the reverse of the drawing order.
      const order = drawOrder(items).reverse();
      const hit = order.find(it => hitItem(it, p.x, p.y));
      if (!hit) { setSel(null); return; }
      const i = items.indexOf(hit);
      setSel(i);
      drag = { i, ox: p.x - hit.x, oy: p.y - hit.y };
      c.setPointerCapture(e.pointerId);
      e.preventDefault();
    };
    const move = (e: PointerEvent) => {
      if (!drag) return;
      const p = toRoom(e), d = drag;
      setRoom(r => ({
        ...r,
        items: r.items.map((it, j) => j === d.i ? { ...it, x: Math.max(0, Math.min(ROOM_W, p.x - d.ox)), y: Math.max(40, Math.min(ROOM_H - 6, p.y - d.oy)) } : it),
      }));
    };
    const up = () => { drag = null; };
    c.addEventListener('pointerdown', down);
    c.addEventListener('pointermove', move);
    c.addEventListener('pointerup', up);
    c.addEventListener('pointercancel', up);
    return () => {
      c.removeEventListener('pointerdown', down);
      c.removeEventListener('pointermove', move);
      c.removeEventListener('pointerup', up);
      c.removeEventListener('pointercancel', up);
    };
  }, []);

  const close = () => {
    if (editing && changed && !window.confirm('Leave without saving your decorating?')) return;
    onClose();
  };

  // Esc leaves (or stops decorating); Delete removes the selected thing.
  useEffect(() => {
    const key = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.stopPropagation(); if (editRef.current) { setEditing(false); setSel(null); setRoom(saved); } else onClose(); }
      if ((e.key === 'Delete' || e.key === 'Backspace') && editRef.current && selRef.current != null && !(e.target instanceof HTMLInputElement)) {
        const i = selRef.current;
        setRoom(r => ({ ...r, items: r.items.filter((_, j) => j !== i) }));
        setSel(null);
      }
    };
    window.addEventListener('keydown', key, true);
    return () => window.removeEventListener('keydown', key, true);
  }, [saved, onClose]);

  const add = (k: ItemId) => {
    if (room.items.length >= MAX_ITEMS) { setError(`A room holds up to ${MAX_ITEMS} things.`); return; }
    setError(null);
    setRoom(r => ({ ...r, items: [...r.items, newItem(k)] }));
    setSel(room.items.length);
  };
  const change = (f: (it: RoomItem) => RoomItem) => {
    if (sel == null) return;
    setRoom(r => ({ ...r, items: r.items.map((it, j) => (j === sel ? f(it) : it)) }));
  };
  const save = async () => {
    if (!onSave) return;
    setSaving(true); setError(null);
    try { await onSave(room); setEditing(false); setSel(null); }
    catch (e) { setError(e instanceof Error ? e.message : 'Could not save.'); }
    finally { setSaving(false); }
  };

  const def = HOUSES[house.kind];
  const selected = sel != null ? room.items[sel] : null;
  const title = editable ? `Your ${def.name}` : house.owner ? `${house.owner}’s ${def.name}` : `A classmate’s ${def.name}`;
  const out = house.fish.length - insideNow;

  return (
    <div className="ocean-modal room-modal" role="dialog" aria-modal="true" aria-label={title}>
      <div className="ocean-card room-card">
        <div className="row">
          <h3>🏠 {title}</h3>
          <span className="small muted">
            {house.fish.length === 0 ? 'No fish live here yet.'
              : `${insideNow} of ${house.fish.length} fish home${out > 0 ? ` · ${out} out swimming` : ''}`}
          </span>
          <span className="spacer" />
          {editable && !editing && <button type="button" onClick={() => setEditing(true)}>🎨 Decorate</button>}
          <button type="button" className="icon" aria-label="Leave the house" onClick={close}>✕</button>
        </div>
        <canvas ref={canvasRef} className={`room-canvas${editing ? ' editing' : ''}`} aria-label={`Inside ${title}`} />
        {!editing && <p className="small muted" style={{ margin: 0 }}>Fish come and go on their own: they swim out through the door to explore around the house, then come back in.</p>}

        {editing && (
          <div className="room-tools">
            <div className="room-row">
              <span className="small muted">Walls</span>
              <div className="chips">
                {(Object.keys(WALLS) as WallId[]).map(w => (
                  <button key={w} type="button" aria-pressed={room.wall === w} className={`swatch${room.wall === w ? ' on' : ''}`} title={WALLS[w].name}
                    style={{ background: `linear-gradient(${WALLS[w].top}, ${WALLS[w].bottom})` }} onClick={() => setRoom(r => ({ ...r, wall: w }))} />
                ))}
              </div>
              <span className="small muted">Floor</span>
              <div className="chips">
                {(Object.keys(FLOORS) as FloorId[]).map(f => (
                  <button key={f} type="button" aria-pressed={room.floor === f} className={`swatch${room.floor === f ? ' on' : ''}`} title={FLOORS[f].name}
                    style={{ background: FLOORS[f].color }} onClick={() => setRoom(r => ({ ...r, floor: f }))} />
                ))}
              </div>
            </div>
            <div className="room-palette" role="toolbar" aria-label="Add to the room">
              {(Object.keys(ITEMS) as ItemId[]).map(k => (
                <button key={k} type="button" className="small" onClick={() => add(k)} title={`Add ${ITEMS[k].name}`}>
                  <span aria-hidden>{ITEMS[k].icon}</span> {ITEMS[k].name}{prices[k] ? <span className="price"> 🪙{prices[k]}</span> : null}
                </button>
              ))}
            </div>
            <div className="room-row">
              {selected ? (
                <>
                  <strong className="small">{ITEMS[selected.k].icon} {ITEMS[selected.k].name}</strong>
                  <button type="button" className="small" aria-label="Smaller" onClick={() => change(it => ({ ...it, s: Math.max(0.5, +(it.s - 0.15).toFixed(2)) }))}>－ Smaller</button>
                  <button type="button" className="small" aria-label="Bigger" onClick={() => change(it => ({ ...it, s: Math.min(2.2, +(it.s + 0.15).toFixed(2)) }))}>＋ Bigger</button>
                  {ITEMS[selected.k].colors > 1 && <button type="button" className="small" onClick={() => change(it => ({ ...it, c: (it.c + 1) % ITEMS[it.k].colors }))}>🎨 Colour</button>}
                  <button type="button" className="small" onClick={() => change(it => ({ ...it, f: !it.f }))}>↔ Flip</button>
                  <button type="button" className="small" onClick={() => { const i = sel!; setRoom(r => ({ ...r, items: r.items.filter((_, j) => j !== i) })); setSel(null); }}>🗑 Remove</button>
                </>
              ) : <span className="small muted">Add things above, then drag them around the room. Tap one to change or remove it.</span>}
              <span className="spacer" />
              <span className="small muted">{room.items.length}/{MAX_ITEMS}</span>
            </div>
            {error && <div className="notice small">{error}</div>}
            <div className="row" style={{ justifyContent: 'flex-end' }}>
              <span className="small muted" style={{ marginRight: 'auto' }}>
                {balance != null && <>You have 🪙 {sandDollars(balance)}. </>}
                {cost > 0 ? (short ? `You need 🪙 ${cost - balance!} more for what you added: remove something, or earn more from your teacher.` : `What you added costs 🪙 ${cost}. Moving, resizing, colours, walls and floors are free.`)
                  : 'Moving things, colours, walls and floors are free. Removing something doesn’t give sand dollars back.'}
              </span>
              <button type="button" onClick={() => { setRoom(saved); setEditing(false); setSel(null); setError(null); }}>Cancel</button>
              <button type="button" className="primary" disabled={saving || !changed || short} onClick={() => void save()}>{saving ? 'Saving…' : cost > 0 ? `Save · 🪙 ${cost}` : 'Save my room'}</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
